import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Run result for a single playlist attempt
 */
export interface RunResult {
  runId: string;
  playlistId: string;
  completedAt: number;
  levelResults: {
    levelId: string;
    attempts: number;
    revealsUsed: number;
    timeMs: number;
    grade: 'Perfect' | 'Good' | 'Off';
    score: number;
  }[];
  totalScore: number;
}

/**
 * Best score per playlist
 */
export interface BestScore {
  playlistId: string;
  score: number;
  achievedAt: number;
  runId: string;
}

/**
 * Profile stats
 */
export interface ProfileStats {
  totalRuns: number;
  totalPerfects: number;
  totalGoods: number;
  currentStreak: number;
  longestStreak: number;
  lastPlayedAt: number | null;
}

/**
 * Profile store state
 */
interface ProfileState {
  // Identity (local until claimed)
  localId: string;
  displayName: string;
  
  // Stats
  stats: ProfileStats;
  
  // History
  runHistory: RunResult[];
  bestScores: BestScore[];
  
  // Actions
  addRun: (result: RunResult) => void;
  updateStreak: () => void;
  setDisplayName: (name: string) => void;
  resetProfile: () => void;
}

/**
 * Generate a local ID for anonymous users
 */
const generateLocalId = () =>
  `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, _get) => ({
      // Initial state
      localId: generateLocalId(),
      displayName: 'Designer',
      
      stats: {
        totalRuns: 0,
        totalPerfects: 0,
        totalGoods: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastPlayedAt: null,
      },
      
      runHistory: [],
      bestScores: [],

      // Actions
      addRun: (result) =>
        set((state) => {
          // Count grades
          const perfects = result.levelResults.filter(
            (l) => l.grade === 'Perfect'
          ).length;
          const goods = result.levelResults.filter(
            (l) => l.grade === 'Good'
          ).length;

          // Update best score for playlist
          const existingBest = state.bestScores.find(
            (b) => b.playlistId === result.playlistId
          );
          let newBestScores = [...state.bestScores];

          if (!existingBest || result.totalScore > existingBest.score) {
            const newBest: BestScore = {
              playlistId: result.playlistId,
              score: result.totalScore,
              achievedAt: result.completedAt,
              runId: result.runId,
            };

            if (existingBest) {
              newBestScores = newBestScores.map((b) =>
                b.playlistId === result.playlistId ? newBest : b
              );
            } else {
              newBestScores.push(newBest);
            }
          }

          return {
            runHistory: [result, ...state.runHistory].slice(0, 100), // Keep last 100
            bestScores: newBestScores,
            stats: {
              ...state.stats,
              totalRuns: state.stats.totalRuns + 1,
              totalPerfects: state.stats.totalPerfects + perfects,
              totalGoods: state.stats.totalGoods + goods,
              lastPlayedAt: result.completedAt,
            },
          };
        }),

      updateStreak: () =>
        set((state) => {
          const now = Date.now();
          const lastPlayed = state.stats.lastPlayedAt;
          const oneDayMs = 24 * 60 * 60 * 1000;

          let newStreak = state.stats.currentStreak;

          if (!lastPlayed) {
            // First play
            newStreak = 1;
          } else if (now - lastPlayed < oneDayMs) {
            // Same day, keep streak
          } else if (now - lastPlayed < 2 * oneDayMs) {
            // Next day, increment streak
            newStreak = state.stats.currentStreak + 1;
          } else {
            // Streak broken
            newStreak = 1;
          }

          return {
            stats: {
              ...state.stats,
              currentStreak: newStreak,
              longestStreak: Math.max(state.stats.longestStreak, newStreak),
            },
          };
        }),

      setDisplayName: (name) => set({ displayName: name }),

      resetProfile: () =>
        set({
          localId: generateLocalId(),
          displayName: 'Designer',
          stats: {
            totalRuns: 0,
            totalPerfects: 0,
            totalGoods: 0,
            currentStreak: 0,
            longestStreak: 0,
            lastPlayedAt: null,
          },
          runHistory: [],
          bestScores: [],
        }),
    }),
    {
      name: 'pyxl-profile',
      version: 1,
    }
  )
);

/**
 * Selector: Get best score for a playlist
 */
export const selectBestScore = (playlistId: string) => (state: ProfileState) =>
  state.bestScores.find((b) => b.playlistId === playlistId);

/**
 * Selector: Get recent runs
 */
export const selectRecentRuns = (limit: number) => (state: ProfileState) =>
  state.runHistory.slice(0, limit);
